import type { OrderDetails } from '@/entities/order/model'
import { Box, Flex, Group, Paper, Stack, Text } from '@mantine/core'
import { IconChevronDown } from '@tabler/icons-react'
import { clsx } from 'clsx'
import { OrderStatusDisplay } from '@/entities/order/ui'
import { getOrderSecondaryMessage, getRestrictedItemsDisplay, hasRestrictedField, useOrderAlert, usePanelOrderStatus } from '@/features/order/lib'
import { formatTime } from '@/shared/lib/dayjs'
import { HsFieldsTable } from '../hs-fields-table/HsFieldsTable'
import { StatusUpdatePopover } from '../status-update/StatusUpdatePopover'
import classes from './OrderDetailsDesktopLayout.module.css'

interface OrderDetailsDesktopLayoutProps {
  order: OrderDetails
  onStatusUpdate?: (status: string) => void
  withoutBackdrop?: boolean
  /**
   * `split` (default) renders the info card and hs-fields table side by side.
   * `stacked` renders them in a column, with the info card laid out as a 2×2
   * auto-flow grid — a POC for wider desktop viewports.
   */
  layout?: 'split' | 'stacked'
}

export function OrderDetailsDesktopLayout({ order, onStatusUpdate, withoutBackdrop, layout = 'split' }: OrderDetailsDesktopLayoutProps) {
  const { displayState } = useOrderAlert(order)
  const { statusText, statusDisplayState, isPanelOrderContext } = usePanelOrderStatus({
    order,
    displayState,
    withoutBackdrop,
  })

  const isStacked = layout === 'stacked'

  const orderNumberBlock = (
    <Stack gap="6px">
      <Text span className={classes.entryLabel}>Order number</Text>
      <Text span className={classes.entryValue}>{order.orderId}</Text>
    </Stack>
  )

  const statusDisplayBlock = (
    <OrderStatusDisplay
      orderStatus={statusText}
      displayState={statusDisplayState}
      secondaryText={isPanelOrderContext ? undefined : getOrderSecondaryMessage(
        order.status,
        order.lastTransitionTime ? formatTime(order.lastTransitionTime, 'HH:mm') : undefined,
      )}
      eta={order.eta}
      etaDirection={order.etaDirection}
      scheduledTimeForCollection={order.scheduledTimeForCollection}
    />
  )

  const statusTriggerBlock = (
    <StatusUpdatePopover
      orderId={order.orderId}
      currentStatus={order.status}
      lastUpdateTime={order.lastUpdateTime}
      onStatusUpdate={onStatusUpdate}
      renderTrigger={({ onClick, isOpen }) => (
        <Box className={classes.statusTrigger} onClick={onClick}>
          <Text span className={classes.entryLabel}>Status</Text>
          <Group gap="xs">
            <Text className={classes.statusItem}>{displayState?.stateDisplayName || order.status}</Text>
            <IconChevronDown
              size={24}
              className={`${classes.statusIcon} ${isOpen ? classes.statusIconRotated : classes.statusIconDefault}`}
            />
          </Group>
        </Box>
      )}
    />
  )

  const restrictedBlock = hasRestrictedField(order)
    ? (
        <Stack gap="6px">
          <Text span className={classes.entryLabel}>Restricted items</Text>
          <Text span className={classes.entryValue}>
            {getRestrictedItemsDisplay(order.hsFields)}
          </Text>
        </Stack>
      )
    : null

  return (
    <Flex className={clsx(classes.contentLayout, isStacked && classes.contentLayoutStacked)}>
      <Paper className={classes.orderInfoCard}>
        {isStacked
          ? (
              <Box className={classes.orderInfoGrid}>
                <div className={classes.gridCell}>{orderNumberBlock}</div>
                <div className={clsx(classes.gridCell, classes.gridCellEnd)}>{statusDisplayBlock}</div>
                <div className={classes.gridCell}>{statusTriggerBlock}</div>
                {restrictedBlock && <div className={clsx(classes.gridCell, classes.gridCellEnd)}>{restrictedBlock}</div>}
              </Box>
            )
          : (
              <Flex direction="column" justify="space-between" gap="xl" h="100%">
                <Group justify="space-between" align="flex-start" wrap="nowrap" w="100%">
                  {orderNumberBlock}
                  {statusDisplayBlock}
                </Group>
                {statusTriggerBlock}
                {restrictedBlock}
              </Flex>
            )}
      </Paper>

      <HsFieldsTable hsFields={order.hsFields} variant="desktop" />
    </Flex>
  )
}
